import { Alert, Button, CircularProgress } from "@mui/material";

/**
 * Import banner for web site sources (web_scraper_provider_design.md §10):
 * crawl progress while scanning, indexing progress while the approved pages
 * are created, and the awaiting-review warning with the counts, the expiry
 * and the Review button (D16).
 */
export interface CrawlSyncState {
    status?: string | null;
    total?: number | null;
    error?: string | null;
    discovered?: number | null;
    fetched?: number | null;
    reviewCounts?: Record<string, number> | null;
}

const number = (value?: number | null): number => value ?? 0;

/**
 * "12 new, 3 modified, 1 removed" — only the buckets that have something in
 * them: a refresh where every page is unchanged, or a single-page re-fetch,
 * would otherwise read as a row of zeros. Shared with the provider
 * properties' crawl list.
 */
export const crawlCountsSummary = (counts?: object | null): string => {
    const c = (counts ?? {}) as Record<string, number | null | undefined>;
    const parts = ["new", "modified", "renamed", "removed", "unchanged", "skipped", "failed"]
        .filter(key => number(c[key]) > 0)
        .map(key => `${number(c[key])} ${key}`);
    return parts.length ? parts.join(", ") : "nothing found";
};

export const CrawlBanner = ({
    providerName,
    host,
    sync,
    expiresAt,
    trigger,
    reviewAvailable,
    onReview
}: {
    providerName: string;
    host?: string;
    sync: CrawlSyncState;
    expiresAt?: string | null;
    /** manual | scheduled | page — a single-page re-fetch is worded as such. */
    trigger?: string | null;
    /** The run id has been resolved; the Review button is enabled. */
    reviewAvailable: boolean;
    onReview: () => void;
}) => {
    const target = host || providerName;
    const singlePage = trigger === "page";
    if (sync.status === "scanning") {
        const progress =
            sync.discovered != null || sync.fetched != null
                ? `${number(sync.discovered)} found, ${number(sync.fetched)} fetched…`
                : "starting…";
        return (
            <Alert
                className="knowledge-base__import-banner"
                severity="info"
                icon={<CircularProgress size={18} aria-label="In progress" />}
            >
                {singlePage
                    ? `Re-fetching one page from ${target}… It stops for your review before anything changes.`
                    : `Crawling ${target}: ${progress} The crawl stops for your review before anything is indexed.`}
            </Alert>
        );
    }
    if (sync.status === "creating") {
        return (
            <Alert
                className="knowledge-base__import-banner"
                severity="info"
                icon={<CircularProgress size={18} aria-label="In progress" />}
            >
                {`Indexing the approved pages from ${target}… They appear below as they are created.`}
            </Alert>
        );
    }
    if (sync.status === "awaiting_review") {
        const summary = crawlCountsSummary(sync.reviewCounts);
        const subject = singlePage ? "the re-fetched page" : "the crawl";
        const expiry = expiresAt ? new Date(expiresAt) : null;
        const expiryText =
            expiry && !Number.isNaN(expiry.getTime())
                ? ` Review by ${expiry.toLocaleDateString()} or the crawl is discarded.`
                : "";
        return (
            <Alert
                className="knowledge-base__import-banner"
                severity="warning"
                action={
                    <Button
                        variant="text"
                        className="primary-button"
                        onClick={onReview}
                        disabled={!reviewAvailable}
                    >
                        Review
                    </Button>
                }
            >
                {`${target}: ${subject} is waiting for your review (${summary}). Nothing is indexed until you approve it.${expiryText}`}
            </Alert>
        );
    }
    return null;
};

export default CrawlBanner;
